import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';
import { BiVizDashboardDecoActivitySqlMap } from '../sql-maps/biviz-dashboard-deco-activity-datasource-sql-map';

export interface DecoActivityByDayNoDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

/**
 * Per-cohort-day (day_no) loader for decoration inflow / new placed decos
 * (dashboard_deco_activity). Mirrors the meta-economy decos loader but sums the
 * `inflow_delta` / `new_placed_delta` columns of this table.
 */
export class DecoActivityByDayNoDataLoaderFactory extends Factory<
    DataLoader,
    DecoActivityByDayNoDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'DecoActivityByDayNoDataLoaderFactory';

    getInstance(options: DecoActivityByDayNoDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const ignoredContextColumnIds = [
                    'ACTIVITY_DATE',
                    'ACTIVITY_APP_VERSION',
                    'ACTIVITY_APP_VERSION_GROUP_MINOR',
                ];

                const decoSqlMap = BiVizDashboardDecoActivitySqlMap.sqlMap;

                const decoDsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    decoSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(decoDsColumns.missing);

                decoDsColumns.present = decoDsColumns.present.filter(
                    (col) => !ignoredContextColumnIds.includes(col.id)
                );

                const decoSQL =
                    BiVizDashboardDecoActivitySqlMap.sqlStatementsCompiler.compileSQLStatements(
                        decoDsColumns.present,
                        queryContext.settings,
                        options
                    );

                const usersdashActivity = BiVizDashboardUsersActivity;
                const usersdashActivityDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        usersdashActivity.sqlMap.columns
                    );
                usersdashActivityDsColumns.present =
                    usersdashActivityDsColumns.present.filter(
                        (col) => !ignoredContextColumnIds.includes(col.id)
                    );
                const usersdashActivitySQL =
                    usersdashActivity.compileSQLStatements(
                        usersdashActivityDsColumns.present,
                        queryContext.settings,
                        options
                    );

                const maxDayNo = '90';
                const win = decoSQL.windowRunning('c');

                // metricName -> source delta column on dashboard_deco_activity
                const metrics = [
                    { name: 'decoration_inflow', delta: 'inflow_delta' },
                    { name: 'new_decos_placed', delta: 'new_placed_delta' },
                ];

                const query = `
-- DataLoaderId: ${this.instanceId}

WITH deco_metrics AS
(
  SELECT
    ${decoSQL.splitSelectConcatCohortAndDatasourceSpecificColumn}
    *
  FROM
  (
    SELECT
      ${decoSQL.cohortSplitSelect}
      ${decoSQL.datasourceSpecificSplitSelect}
      player_day.day_no
      ${metrics
          .flatMap((m) => [
              `, SUM(${m.delta}) AS ${m.name}`,
              `, APPROX_QUANTILES(${m.delta}, 100)[OFFSET(50)] AS median_${m.name}_per_user`,
          ])
          .join('\n      ')}
    FROM \`${generateTableFullPath(
        options.gcpProjectId,
        decoSqlMap.dataset,
        decoSqlMap.tableId
    )}\` ds
    ${decoSQL.join}
    ${decoSQL.where}
    GROUP BY ${decoSQL.cohortSplitGroupBy}${
        decoSQL.datasourceSpecificSplitGroupBy
    }day_no
  )
)

, cohort_base AS
(
    SELECT
      ${
          usersdashActivitySQL.splitGroupBy != ''
              ? `${usersdashActivitySQL.splitSelect.replace(
                    'AS split_group',
                    'AS cohort_split_group'
                )}`
              : ''
      }
      ds.*
      , ${maxDayNo} AS max_day_no
    FROM \`${generateTableFullPath(
        options.gcpProjectId,
        usersdashActivity.sqlMap.dataset,
        usersdashActivity.sqlMap.tableId
    )}\` ds
    ${usersdashActivitySQL.join}
    ${usersdashActivitySQL.where}
)

, cohort_size AS
(
  SELECT
    ${decoSQL.splitGroupBy}
    day_no,
    SUM(cohort_size) AS cohort_size
  FROM
  (
    SELECT
      ${decoSQL.splitSelectConcatCohortAndDatasourceSpecificColumn}
      ${decoSqlMap.columns.FIREBASE_FIRST_OPEN_DATE?.columnSQL},
      COUNT(DISTINCT player_id) AS cohort_size,
      GENERATE_ARRAY(0, max_day_no ) AS days
    FROM
    (
      SELECT cohort_base.*
      FROM cohort_base
      WHERE day_no = 0
    )
    ${
        decoSQL.is_in_context_split_datasource_specific_columns
            ? `CROSS JOIN (SELECT DISTINCT deco_split_group FROM deco_metrics)`
            : ''
    }
    GROUP BY ${decoSQL.splitGroupBy}${
        decoSqlMap.columns.FIREBASE_FIRST_OPEN_DATE?.columnSQL
    }, max_day_no
  ), UNNEST(days) AS day_no
  GROUP BY ${decoSQL.splitGroupBy}day_no
)

, by_fod AS
(
SELECT
  ${decoSQL.splitGroupBy != '' ? 'c.split_group,' : ''}
  c.day_no
  ${metrics
      .flatMap((m) => [
          // raw sum
          `, b.${m.name}`,
          // median (direct)
          `, b.median_${m.name}_per_user`,
          // running avg per user
          `, ROUND(SUM(SAFE_DIVIDE(b.${m.name}, c.cohort_size)) OVER (${win}), 3) AS running_avg_${m.name}_per_user`,
          // running median per user
          `, ROUND(SUM(b.median_${m.name}_per_user) OVER (${win}), 3) AS running_median_${m.name}_per_user`,
      ])
      .join('\n  ')}
  FROM cohort_size c
  LEFT JOIN deco_metrics b
    ON c.day_no = b.day_no
    ${decoSQL.splitGroupBy != '' ? `AND c.split_group = b.split_group` : ''}
)

SELECT * FROM by_fod
ORDER BY ${decoSQL.splitGroupBy}day_no
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                this.emitter.emit({
                    data: this.latestData,
                    queryContext: queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
